/**
 * Rate Limiter Utility
 * Implements token bucket algorithm for client-side rate limiting
 * Uses localStorage to persist rate limit state across page refreshes
 */

export interface RateLimitConfig {
  /** Maximum number of requests allowed */
  maxRequests: number;
  /** Time window in milliseconds */
  windowMs: number;
  /** Unique identifier for this rate limit (e.g., 'createOrder', 'addToCart') */
  key: string;
  /** Optional: Use sessionStorage instead of localStorage (cleared on tab close) */
  useSessionStorage?: boolean;
}

export interface RateLimitResult {
  /** Whether the request is allowed */
  allowed: boolean;
  /** Number of requests remaining in the current window */
  remaining: number;
  /** Time until the rate limit resets (in milliseconds) */
  resetTime: number;
  /** Total number of requests allowed */
  limit: number;
}

/**
 * Rate limiter class implementing token bucket algorithm
 */
class RateLimiter {
  private storage: Storage;

  constructor(useSessionStorage: boolean = false) {
    this.storage = useSessionStorage ? sessionStorage : localStorage;
  }

  /**
   * Check if a request is allowed based on rate limit configuration
   * @param config - Rate limit configuration
   * @returns Rate limit result
   */
  checkLimit(config: RateLimitConfig): RateLimitResult {
    const storageKey = `rateLimit_${config.key}`;
    const now = Date.now();
    
    try {
      const stored = this.storage.getItem(storageKey);
      let bucket: { tokens: number; lastRefill: number };

      if (stored) {
        try {
          bucket = JSON.parse(stored);
        } catch {
          // Invalid stored data, reset bucket
          bucket = { tokens: config.maxRequests, lastRefill: now };
        }
      } else {
        bucket = { tokens: config.maxRequests, lastRefill: now };
      }

      // Calculate tokens to add based on time elapsed
      const timeElapsed = now - bucket.lastRefill;
      const tokensToAdd = Math.floor((timeElapsed / config.windowMs) * config.maxRequests);
      
      // Refill tokens (but don't exceed max)
      bucket.tokens = Math.min(config.maxRequests, bucket.tokens + tokensToAdd);
      bucket.lastRefill = now;

      // Check if request is allowed
      const allowed = bucket.tokens >= 1;
      
      if (allowed) {
        bucket.tokens -= 1;
      }

      // Save updated bucket
      this.storage.setItem(storageKey, JSON.stringify(bucket));

      // Calculate reset time
      const resetTime = allowed 
        ? config.windowMs - (timeElapsed % config.windowMs)
        : config.windowMs - (timeElapsed % config.windowMs);

      return {
        allowed,
        remaining: Math.max(0, Math.floor(bucket.tokens)),
        resetTime,
        limit: config.maxRequests,
      };
    } catch (error) {
      // If storage fails (e.g., quota exceeded, private browsing), allow the request
      // but log a warning
      console.warn('Rate limiter storage error:', error);
      return {
        allowed: true,
        remaining: config.maxRequests - 1,
        resetTime: config.windowMs,
        limit: config.maxRequests,
      };
    }
  }

  /**
   * Reset rate limit for a specific key
   * @param key - Rate limit key to reset
   */
  reset(key: string): void {
    const storageKey = `rateLimit_${key}`;
    this.storage.removeItem(storageKey);
  }

  /**
   * Get current rate limit status without consuming a token
   * @param config - Rate limit configuration
   * @returns Rate limit result
   */
  getStatus(config: RateLimitConfig): RateLimitResult {
    const storageKey = `rateLimit_${config.key}`;
    const now = Date.now();
    
    try {
      const stored = this.storage.getItem(storageKey);
      let bucket: { tokens: number; lastRefill: number };

      if (stored) {
        try {
          bucket = JSON.parse(stored);
        } catch {
          bucket = { tokens: config.maxRequests, lastRefill: now };
        }
      } else {
        bucket = { tokens: config.maxRequests, lastRefill: now };
      }

      const timeElapsed = now - bucket.lastRefill;
      const tokensToAdd = Math.floor((timeElapsed / config.windowMs) * config.maxRequests);
      bucket.tokens = Math.min(config.maxRequests, bucket.tokens + tokensToAdd);

      const resetTime = config.windowMs - (timeElapsed % config.windowMs);

      return {
        allowed: bucket.tokens >= 1,
        remaining: Math.max(0, Math.floor(bucket.tokens)),
        resetTime,
        limit: config.maxRequests,
      };
    } catch (error) {
      console.warn('Rate limiter storage error:', error);
      return {
        allowed: true,
        remaining: config.maxRequests,
        resetTime: config.windowMs,
        limit: config.maxRequests,
      };
    }
  }
}

// Default rate limiter instance
const defaultRateLimiter = new RateLimiter(false);

/**
 * Rate limit error class
 */
export class RateLimitError extends Error {
  constructor(
    public readonly remaining: number,
    public readonly resetTime: number,
    public readonly limit: number,
    message?: string
  ) {
    super(message || `Rate limit exceeded. Try again in ${Math.ceil(resetTime / 1000)} seconds.`);
    this.name = 'RateLimitError';
  }
}

/**
 * Apply rate limiting to an async function
 * @param fn - Function to wrap with rate limiting
 * @param config - Rate limit configuration
 * @returns Wrapped function with rate limiting
 */
export function withRateLimit<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  config: RateLimitConfig
): T {
  return (async (...args: Parameters<T>) => {
    const limiter = config.useSessionStorage 
      ? new RateLimiter(true) 
      : defaultRateLimiter;
    
    const result = limiter.checkLimit(config);
    
    if (!result.allowed) {
      throw new RateLimitError(
        result.remaining,
        result.resetTime,
        result.limit,
        `Rate limit exceeded for ${config.key}. Please try again in ${Math.ceil(result.resetTime / 1000)} seconds.`
      );
    }

    try {
      return await fn(...args);
    } catch (error) {
      // If the function fails, we don't refund the token
      // This prevents abuse where users intentionally cause errors to bypass rate limits
      throw error;
    }
  }) as T;
}

/**
 * Check rate limit and throw error if exceeded
 * @param config - Rate limit configuration
 * @throws RateLimitError if rate limit is exceeded
 */
export function checkRateLimit(config: RateLimitConfig): void {
  const limiter = config.useSessionStorage 
    ? new RateLimiter(true) 
    : defaultRateLimiter;
  
  const result = limiter.checkLimit(config);
  
  if (!result.allowed) {
    throw new RateLimitError(
      result.remaining,
      result.resetTime,
      result.limit
    );
  }
}

/**
 * Get rate limit status without consuming a token
 * @param config - Rate limit configuration
 * @returns Rate limit status
 */
export function getRateLimitStatus(config: RateLimitConfig): RateLimitResult {
  const limiter = config.useSessionStorage 
    ? new RateLimiter(true) 
    : defaultRateLimiter;
  
  return limiter.getStatus(config);
}

/**
 * Reset rate limit for a specific key
 * @param key - Rate limit key to reset
 * @param useSessionStorage - Whether to use sessionStorage
 */
export function resetRateLimit(key: string, useSessionStorage: boolean = false): void {
  const limiter = useSessionStorage ? new RateLimiter(true) : defaultRateLimiter;
  limiter.reset(key);
}

/**
 * Predefined rate limit configurations for common operations
 */
export const RATE_LIMITS = {
  // Contact form submissions: 3 per hour
  CREATE_QUERY: {
    maxRequests: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
    key: 'createQuery',
  },
  
  // Order creation: 10 per hour
  CREATE_ORDER: {
    maxRequests: 10,
    windowMs: 60 * 60 * 1000, // 1 hour
    key: 'createOrder',
  },
  
  // Cart operations: 30 per minute
  CART_OPERATION: {
    maxRequests: 30,
    windowMs: 60 * 1000, // 1 minute
    key: 'cartOperation',
  },
  
  // Add to cart: 20 per minute
  ADD_TO_CART: {
    maxRequests: 20,
    windowMs: 60 * 1000, // 1 minute
    key: 'addToCart',
  },
  
  // Notify me: 10 per hour
  NOTIFY_ME: {
    maxRequests: 10,
    windowMs: 60 * 60 * 1000, // 1 hour
    key: 'notifyMe',
  },
  
  // Shipping address operations: 20 per hour
  SHIPPING_ADDRESS: {
    maxRequests: 20,
    windowMs: 60 * 60 * 1000, // 1 hour
    key: 'shippingAddress',
  },
  
  // Payment operations: 5 per hour
  PAYMENT: {
    maxRequests: 5,
    windowMs: 60 * 60 * 1000, // 1 hour
    key: 'payment',
  },
  
  // Firestore read operations: 100 per minute
  FIRESTORE_READ: {
    maxRequests: 100,
    windowMs: 60 * 1000, // 1 minute
    key: 'firestoreRead',
  },
  
  // Firestore write operations: 30 per minute
  FIRESTORE_WRITE: {
    maxRequests: 30,
    windowMs: 60 * 1000, // 1 minute
    key: 'firestoreWrite',
  },
  
  // Firestore create operations: 20 per minute
  FIRESTORE_CREATE: {
    maxRequests: 20,
    windowMs: 60 * 1000, // 1 minute
    key: 'firestoreCreate',
  },
  
  // Firestore update operations: 30 per minute
  FIRESTORE_UPDATE: {
    maxRequests: 30,
    windowMs: 60 * 1000, // 1 minute
    key: 'firestoreUpdate',
  },
  
  // Firestore delete operations: 10 per minute
  FIRESTORE_DELETE: {
    maxRequests: 10,
    windowMs: 60 * 1000, // 1 minute
    key: 'firestoreDelete',
  },
  
  // Password reset: 3 per hour
  PASSWORD_RESET: {
    maxRequests: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
    key: 'passwordReset',
  },
  
  // Sign in attempts: 5 per 15 minutes
  SIGN_IN: {
    maxRequests: 5,
    windowMs: 15 * 60 * 1000, // 15 minutes
    key: 'signIn',
  },
  
  // Registration: 3 per hour
  REGISTER: {
    maxRequests: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
    key: 'register',
  },
} as const;

